import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Header,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    BellIcon,
    HomeIcon
} from "../../anatomy/icons";

const NAME = "Header";

const USAGE_CODE = `const [isScrolled, setIsScrolled] = useState(false);

<Header
    actions={[
        {
            accessibilityLabel: "Search",
            onPress: openSearch,
            icon: SearchIcon
        }
    ]}
    isScrolled={isScrolled}
    navigation={navigation}
    isGoBackEnabled={true}
    title="Messages"
/>

<PageContainer
    onScrollEdgeChange={setIsScrolled}
    isScrollable={true}
>
    {content}
</PageContainer>`;

const PREVIEW_NAVIGATION = {
    goBack: () => undefined,
    canGoBack: () => true
};

const getColumnStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    flexDirection: "column",
    gap: spaces.spacingSm,
    display: "flex",
    width: "100%"
});

const getContentStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    padding: spaces.spacingMd
});

const TITLE_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("headerPage.slotsTitle"),
        id: "slots"
    },
    {
        title: t("headerPage.scrollTitle"),
        id: "scroll"
    },
    {
        title: t("headerPage.spaceTitle"),
        id: "space"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const HeaderPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        lastPress,
        setLastPress
    ] = useState("");

    const handleBack = () => setLastPress(t("headerPage.back"));

    const handleSearch = () => setLastPress(t("sample.search"));

    const handleNotifications = () => setLastPress(t("headerPage.notifications"));

    const navigation = {
        canGoBack: () => true,
        goBack: handleBack
    };

    const lastPressText = lastPress ? t("headerPage.lastPress", [
        lastPress
    ]) : t("headerPage.noPress");

    const renderContent = () => <div
        style={getContentStyle(docsTokens)}
    >
        <Text
            variant="bodyMediumSize"
            color="low"
        >
            {t("headerPage.pageContent")}
        </Text>
    </div>;

    const renderFrame = (label: string, header: ReactNode) => <div
        style={getColumnStyle(docsTokens)}
        key={label}
    >
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
        <DemoFrame>
            {header}
            {renderContent()}
        </DemoFrame>
    </div>;

    const renderLeftSlot = () => <Button
        accessibilityLabel={t("sample.home")}
        onPress={() => undefined}
        spreadBehaviour="free"
        variant="ghost"
        icon={HomeIcon}
        type="neutral"
        size="large"
    />;

    const renderTitleSlot = () => <div
        style={TITLE_STYLE}
    >
        <Text
            variant="titleMediumSize"
            color="high"
        >
            {t("headerPage.orderTitle")}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t("headerPage.orderStatus")}
        </Text>
    </div>;

    const renderRightSlot = () => <Button
        accessibilityLabel={t("sample.search")}
        onPress={() => undefined}
        spreadBehaviour="free"
        icon={SearchIcon}
        variant="ghost"
        type="neutral"
        size="large"
    />;

    return <Article>
        <ComponentHeader
            lead={t("headerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("headerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("headerPage.overviewCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <Header
                        actions={[
                            {
                                accessibilityLabel: t("sample.search"),
                                onPress: handleSearch,
                                icon: SearchIcon
                            },
                            {
                                accessibilityLabel: t("headerPage.notifications"),
                                onPress: handleNotifications,
                                icon: BellIcon
                            }
                        ]}
                        title={t("headerPage.screenTitle")}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        navigation={navigation}
                        isGoBackEnabled={true}
                    />
                    {renderContent()}
                </DemoFrame>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {lastPressText}
                </Text>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("headerPage.slotsTitle")}
            id="slots"
        >
            <Paragraph>
                {t("headerPage.slotsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderFrame(t("headerPage.slotRoot"), <Header
                        title={t("headerPage.screenTitle")}
                        actions={[
                            {
                                title: t("sample.action"),
                                onPress: () => undefined,
                                variant: "filled",
                                type: "primary"
                            }
                        ]}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                    />)
                }
                {
                    renderFrame("renderLeft · renderTitle · renderRight", <Header
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        renderTitle={renderTitleSlot}
                        renderRight={renderRightSlot}
                        renderLeft={renderLeftSlot}
                    />)
                }
                {
                    renderFrame(t("headerPage.slotLong"), <Header
                        actions={[
                            {
                                accessibilityLabel: t("sample.search"),
                                onPress: () => undefined,
                                icon: SearchIcon
                            },
                            {
                                accessibilityLabel: t("headerPage.notifications"),
                                onPress: () => undefined,
                                icon: BellIcon
                            }
                        ]}
                        title={t("headerPage.longTitle")}
                        navigation={PREVIEW_NAVIGATION}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        isGoBackEnabled={true}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("headerPage.scrollTitle")}
            id="scroll"
        >
            <Paragraph>
                {t("headerPage.scrollText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderFrame("isScrolled={undefined}", <Header
                        title={t("headerPage.screenTitle")}
                        navigation={PREVIEW_NAVIGATION}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        isScrolled={undefined}
                        isGoBackEnabled={true}
                    />)
                }
                {
                    renderFrame("isScrolled={false}", <Header
                        title={t("headerPage.screenTitle")}
                        navigation={PREVIEW_NAVIGATION}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        isGoBackEnabled={true}
                        isScrolled={false}
                    />)
                }
                {
                    renderFrame("isScrolled={true}", <Header
                        title={t("headerPage.screenTitle")}
                        navigation={PREVIEW_NAVIGATION}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        isGoBackEnabled={true}
                        isScrolled={true}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("headerPage.spaceTitle")}
            id="space"
        >
            <Paragraph>
                {t("headerPage.spaceText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderFrame("isWorkWithHeaderSpace={false}", <Header
                        title={t("headerPage.screenTitle")}
                        navigation={PREVIEW_NAVIGATION}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        isGoBackEnabled={true}
                    />)
                }
                {
                    renderFrame("headerSpace={24}", <Header
                        headerSpace={docsTokens.spaces.spacingLg}
                        title={t("headerPage.screenTitle")}
                        navigation={PREVIEW_NAVIGATION}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={true}
                        isGoBackEnabled={true}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("headerPage.a11yTitle"),
                    t("headerPage.a11yBack"),
                    t("headerPage.a11yActions"),
                    t("headerPage.a11yKeyboard"),
                    t("headerPage.a11yDirection"),
                    t("headerPage.a11yMisc")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("headerPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("headerPage.propTitleProps"),
                        name: "titleProps",
                        type: "ITextProps"
                    },
                    {
                        description: t("headerPage.propRenderTitle"),
                        type: "({ navigation }) => ReactNode",
                        name: "renderTitle"
                    },
                    {
                        description: t("headerPage.propRenderLeft"),
                        type: "({ navigation }) => ReactNode",
                        name: "renderLeft"
                    },
                    {
                        description: t("headerPage.propRenderRight"),
                        type: "({ navigation }) => ReactNode",
                        name: "renderRight"
                    },
                    {
                        description: t("headerPage.propActions"),
                        type: "Array<IButtonProps>",
                        name: "actions"
                    },
                    {
                        type: "{ canGoBack: () => boolean; goBack: () => void }",
                        description: t("headerPage.propNavigation"),
                        name: "navigation"
                    },
                    {
                        description: t("headerPage.propIsGoBackEnabled"),
                        name: "isGoBackEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("headerPage.propIsScrolled"),
                        name: "isScrolled",
                        type: "boolean"
                    },
                    {
                        description: t("headerPage.propBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        defaultValue: "\"default\"",
                        name: "backgroundColor"
                    },
                    {
                        description: t("headerPage.propSafeAreaViewBackgroundColor"),
                        name: "safeAreaViewBackgroundColor",
                        type: "keyof ContainerContentColors"
                    },
                    {
                        description: t("headerPage.propIsWorkWithHeaderSpace"),
                        name: "isWorkWithHeaderSpace",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("headerPage.propHeaderSpace"),
                        name: "headerSpace",
                        type: "number"
                    },
                    {
                        description: t("headerPage.propIsWrapSafeAreaContext"),
                        name: "isWrapSafeAreaContext",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("headerPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("headerPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("headerPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("headerPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default HeaderPage;
